// src/hooks/useCardContentDensity.ts
// =====================================================================
// Mede a altura REAL do conteúdo renderizado de cada lado do flashcard
// (frente/verso) e expõe o quanto de espaço livre sobra dentro do card
// através da variável CSS `--gr-empty` (0 = conteúdo preenche o card,
// 1 = card quase vazio). O CSS (só em mobile/tablet — ver StudyPage.css)
// usa essa variável para ajustar fonte e posicionamento de forma contínua,
// sem precisar classificar o flashcard por tipo (V/F, curto, longo...):
// a própria altura renderizada já reflete a densidade do conteúdo.
//
// Cards com imagem são deliberadamente ignorados (a imagem já preenche o
// espaço vertical, então não faz sentido também aumentar a fonte do texto
// ou empurrar o conteúdo pra baixo por cima disso).
// =====================================================================

import { useCallback, useEffect, useRef } from 'react';

function measureSide(contentEl: HTMLElement | null) {
    if (!contentEl) return;

    // `.card-content` é o pai direto do div injetado via dangerouslySetInnerHTML
    // (ver StudyPage.tsx) — é nele que a variável CSS é lida pelas regras
    // responsivas, e sua altura (clientHeight) é o "orçamento" vertical real
    // do card, estável independente do quanto de padding a própria adaptação
    // aplicar (box-sizing: border-box + height: 100% herdado do flashcard).
    const box = contentEl.parentElement;
    if (!box) return;

    // Qualquer <img> dentro do card — seja inline no HTML rico, seja via
    // CardMediaRenderer (.cdmngr-image) — já ocupa espaço vertical por conta
    // própria. Não adaptar fonte/posição nesses casos.
    const hasImage = !!box.querySelector('img');
    if (hasImage) {
        box.style.removeProperty('--gr-empty');
        return;
    }

    const availableHeight = box.clientHeight;
    const contentHeight = contentEl.scrollHeight;
    if (!availableHeight || !contentHeight) return;

    const filled = Math.min(contentHeight / availableHeight, 1);
    const empty = Math.max(0, 1 - filled);
    box.style.setProperty('--gr-empty', empty.toFixed(3));
}

export function useCardContentDensity() {
    const frontContentRef = useRef<HTMLDivElement>(null);
    const backContentRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);

    // rAF garante que a medição rode depois do navegador aplicar o layout
    // resultante da troca de card/flip (ex.: o lado que estava com
    // display:none no mobile só tem altura real depois de virar visível).
    const remeasure = useCallback(() => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
            measureSide(frontContentRef.current);
            measureSide(backContentRef.current);
        });
    }, []);

    useEffect(() => {
        window.addEventListener('resize', remeasure);
        window.addEventListener('orientationchange', remeasure);
        return () => {
            window.removeEventListener('resize', remeasure);
            window.removeEventListener('orientationchange', remeasure);
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
    }, [remeasure]);

    return { frontContentRef, backContentRef, remeasure };
}
